import { DestroyRef, inject, Service, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { FEATURE_FLAGS } from '../../../core/common/constants/feature-flags.constants';
import { FeatureFlagsService } from '../../../core/common/services/feature-flags/feature-flags.service';
import { LookupOptionItem } from './lookup-field-item';
import { LookupResource } from './lookup-resource.model';
import { LookupResourceNavigationService } from './lookup-resource-navigation.service';

/**
 * Per-lookup orchestration for clickable `relation_resource` links.
 *
 * Provided on `LookupFieldComponent` (not root) so each field keeps its own
 * `extraParamValues` mirror and subscriptions — lookups in the same form
 * cannot overwrite each other's param state.
 */
@Service({ autoProvided: false })
export class LookupResourceLinkService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly featureFlagService = inject(FeatureFlagsService);
  private readonly lookupResourceNavigation = inject(
    LookupResourceNavigationService
  );

  /**
   * Sibling / additional-key control values referenced by
   * `relation_resource.extra_params`, mirrored so URL consumers recompute
   * when they change.
   */
  private readonly extraParamValues = signal<Record<string, unknown>>({});

  /** Readonly view for reactive consumers (e.g. a component `computed`). */
  readonly extraParamState = this.extraParamValues.asReadonly();

  private readonly isClickableEnabled = this.featureFlagService.getFeatureFlagValue(
    FEATURE_FLAGS.SYSTEM.make_lockups_clickable
  );

  /**
   * Seeds and keeps in sync values that `relation_resource.extra_params`
   * references as `:control_name`. Matches how show-condition / additional
   * keys resolve controls:
   * - top-level sibling (`project_id`)
   * - additional key on this lookup (`{fieldKey}.project_id`)
   */
  watchResourceExtraParams(
    relationResource: LookupResource | null | undefined,
    fieldKey: string | number,
    formGroup: FormGroup | undefined,
    seedOption?: LookupOptionItem
  ): void {
    const extraParams = relationResource?.extra_params;
    if (!extraParams || !formGroup) {
      return;
    }

    Object.values(extraParams).forEach(paramValue => {
      if (typeof paramValue !== 'string' || !paramValue.startsWith(':')) {
        return;
      }

      const controlName = paramValue.slice(1);
      this.watchExtraParamControl(formGroup, controlName);
      this.watchExtraParamControl(
        formGroup,
        `${fieldKey}.${controlName}`,
        controlName
      );
    });

    this.syncExtraParamsFromOption(relationResource, seedOption);
  }

  /**
   * When a resource is selected, properties referenced by `extra_params`
   * may live on the option and also be patched onto `{fieldKey}.{key}`
   * controls. Mirror any of those that `extra_params` asks for as `:key`.
   */
  syncExtraParamsFromOption(
    relationResource: LookupResource | null | undefined,
    option: LookupOptionItem | undefined
  ): void {
    const extraParams = relationResource?.extra_params;
    if (!extraParams || !option) {
      return;
    }

    const optionRecord = option as unknown as Record<string, unknown>;
    Object.values(extraParams).forEach(paramValue => {
      if (typeof paramValue !== 'string' || !paramValue.startsWith(':')) {
        return;
      }

      const key = paramValue.slice(1);
      if (key in optionRecord) {
        this.setExtraParamValue(key, optionRecord[key]);
      }
    });
  }

  /** Clears dynamic extra_params that were sourced from the selected option. */
  clearOptionDerivedExtraParams(
    relationResource: LookupResource | null | undefined
  ): void {
    const extraParams = relationResource?.extra_params;
    if (!extraParams) {
      return;
    }

    Object.values(extraParams).forEach(paramValue => {
      if (typeof paramValue !== 'string' || !paramValue.startsWith(':')) {
        return;
      }
      this.setExtraParamValue(paramValue.slice(1), null);
    });
  }

  /**
   * Absolute URL of the looked-up resource details page, or `null` when the
   * lookup is not linkable. Read `extraParamState` in the same reactive
   * context before calling so sibling control changes recompute the URL.
   */
  buildResourceUrl(
    relationResource: LookupResource | null | undefined,
    resourceId: string | number | null | undefined,
    fieldKey: string | number,
    formGroup: FormGroup | undefined,
    displayOption: LookupOptionItem | undefined
  ): string | null {
    if (!this.isClickableEnabled) {
      return null;
    }

    const paramValues = this.extraParamValues();
    return this.lookupResourceNavigation.buildResourceUrl(
      relationResource,
      resourceId,
      controlName =>
        this.resolveExtraParam(
          controlName,
          displayOption,
          paramValues,
          fieldKey,
          formGroup
        )
    );
  }

  /**
   * Watches a form control and mirrors its value into `extraParamValues`
   * under `signalKey` (defaults to the control name).
   */
  private watchExtraParamControl(
    formGroup: FormGroup,
    controlName: string,
    signalKey: string = controlName
  ): void {
    const control =
      formGroup.get(controlName) ?? formGroup.controls[controlName];
    if (!control) {
      return;
    }

    this.setExtraParamValue(signalKey, control.value);
    control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => this.setExtraParamValue(signalKey, value));
  }

  private setExtraParamValue(controlName: string, value: unknown): void {
    this.extraParamValues.update(current => ({
      ...current,
      [controlName]: value,
    }));
  }

  /**
   * Resolves a dynamic `:control_name` from, in order:
   * 1. watched sibling / additional-key signal values
   * 2. top-level form control
   * 3. `{fieldKey}.{controlName}` additional-key control (show-condition style)
   * 4. property on the selected lookup option
   */
  private resolveExtraParam(
    controlName: string,
    displayOption: LookupOptionItem | undefined,
    paramValues: Record<string, unknown>,
    fieldKey: string | number,
    formGroup: FormGroup | undefined
  ): unknown {
    if (this.hasExtraParamValue(paramValues[controlName])) {
      return paramValues[controlName];
    }

    const direct = formGroup?.get(controlName)?.value;
    if (this.hasExtraParamValue(direct)) {
      return direct;
    }

    const nestedControlName = `${fieldKey}.${controlName}`;
    const nested =
      formGroup?.get(nestedControlName)?.value ??
      formGroup?.controls[nestedControlName]?.value;
    if (this.hasExtraParamValue(nested)) {
      return nested;
    }

    const fromOption = (
      displayOption as unknown as Record<string, unknown> | undefined
    )?.[controlName];
    if (this.hasExtraParamValue(fromOption)) {
      return fromOption;
    }

    return paramValues[controlName];
  }

  private hasExtraParamValue(value: unknown): boolean {
    return value !== null && value !== undefined && value !== '';
  }
}
